// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, CircularProgress, FormControl, Grid, InputLabel,
  MenuItem, Paper, Select, TextField, Typography,
} from '@mui/material';
import api from '../../utils/api';
import { NOT_PROVIDED, billingErrorMessage } from '../../utils/formatFacturation';
import { CHANNEL_LABEL, ENTITY_KINDS, PUBLIC_ENTITY_KINDS, Empty } from './commun';

// Les seules valeurs que la route accepte (invoices.py : 400 sinon), alors que
// le schéma Pydantic en accepte 120. Choix fermé, pas un champ libre.
const PAYMENT_TERMS = [
  { v: 30, l: '30 jours (pouvoir adjudicateur, EHPAD public autonome)' },
  { v: 50, l: '50 jours (établissement public de santé)' },
  { v: 60, l: '60 jours (plafond contractuel privé)' },
];

const EMPTY_IDENTITY = {
  legal_name: '', legal_form: '', entity_kind: '', siret: '', vat_number: '',
  billing_email: '', billing_address: '', billing_city: '', billing_postal_code: '',
};

const EMPTY_SITE_REFERENCES = {
  siret: '', chorus_service_code: '', chorus_service_code_required: '',
  engagement_number: '', chorus_engagement_required: '', market_number: '',
  payment_terms_days: '', billing_contact_name: '', billing_contact_email: '',
};

function emptyToNull(v) {
  if (v === '' || v === undefined) return null;
  return v;
}

/**
 * N'envoie que ce qui a changé. Les deux PUT appliquent `exclude_unset` : un
 * champ présent dans le corps et valant null efface la donnée. Envoyer le
 * formulaire entier effacerait tout champ mal relu.
 */
function bodyDiff(initial, courant) {
  const sortie = {};
  Object.keys(courant).forEach((k) => {
    const a = emptyToNull(initial[k]);
    const b = emptyToNull(courant[k]);
    if (a !== b) sortie[k] = b;
  });
  return sortie;
}

function triState(v) {
  if (v === true) return 'oui';
  if (v === false) return 'non';
  return '';
}

function toBool(v) {
  if (v === 'oui') return true;
  if (v === 'non') return false;
  return null;
}

/**
 * Onglet « Références client » : identité de facturation d'une organisation et
 * paramètres de facturation de chacun de ses établissements.
 */
export default function ReferencesTab({ orgs, onRechargerOrgs: onReloadOrgs }) {
  const [orgId, setOrgId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [identity, setIdentity] = useState(null);
  const [orgForm, setOrgForm] = useState(EMPTY_IDENTITY);
  const [initOrg, setInitialOrg] = useState(EMPTY_IDENTITY);
  const [siteBilling, setSiteBilling] = useState(null);
  const [formSite, setFormSite] = useState(EMPTY_SITE_REFERENCES);
  const [initSite, setInitSite] = useState(EMPTY_SITE_REFERENCES);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const org = orgs.find((o) => o.id === orgId);
  const sites = org?.sites || [];

  const loadIdentity = useCallback(async (id) => {
    if (!id) { setIdentity(null); setOrgForm(EMPTY_IDENTITY); setInitialOrg(EMPTY_IDENTITY); return; }
    setLoading(true); setError('');
    try {
      const r = await api.get(`/invoices/organisations/${id}/identite`);
      setIdentity(r.data);
      const f = {};
      Object.keys(EMPTY_IDENTITY).forEach((k) => { f[k] = r.data[k] === null || r.data[k] === undefined ? '' : r.data[k]; });
      setOrgForm(f); setInitialOrg(f);
    } catch (e) {
      setError(billingErrorMessage(e, 'Identité de facturation illisible'));
      setIdentity(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const chargerSite = useCallback(async (id) => {
    if (!id) { setSiteBilling(null); setFormSite(EMPTY_SITE_REFERENCES); setInitSite(EMPTY_SITE_REFERENCES); return; }
    setLoading(true); setError('');
    try {
      const r = await api.get(`/invoices/sites/${id}/facturation`);
      setSiteBilling(r.data);
      const f = { ...EMPTY_SITE_REFERENCES };
      Object.keys(EMPTY_SITE_REFERENCES).forEach((k) => {
        if (k.endsWith('_required')) f[k] = triState(r.data[k]);
        else f[k] = r.data[k] === null || r.data[k] === undefined ? '' : r.data[k];
      });
      setFormSite(f); setInitSite(f);
    } catch (e) {
      setError(billingErrorMessage(e, 'Paramètres de facturation du site illisibles'));
      setSiteBilling(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadIdentity(orgId); setSiteId(''); chargerSite(''); }, [orgId, loadIdentity, chargerSite]);
  useEffect(() => { chargerSite(siteId); }, [siteId, chargerSite]);

  const saveOrg = async () => {
    const body = bodyDiff(initOrg, orgForm);
    if (!Object.keys(body).length) { setSuccess('Rien n\'a changé, rien n\'a été envoyé.'); return; }
    setSending(true); setError(''); setSuccess('');
    try {
      const r = await api.put(`/invoices/organisations/${orgId}/identite`, body);
      setIdentity(r.data);
      const f = {};
      Object.keys(EMPTY_IDENTITY).forEach((k) => { f[k] = r.data[k] === null || r.data[k] === undefined ? '' : r.data[k]; });
      setOrgForm(f); setInitialOrg(f);
      setSuccess(`Identité enregistrée : ${Object.keys(body).join(', ')}.`);
      onReloadOrgs();
    } catch (e) {
      setError(billingErrorMessage(e, 'Enregistrement refusé'));
    } finally {
      setSending(false);
    }
  };

  const saveSite = async () => {
    const brut = bodyDiff(initSite, formSite);
    const body = {};
    Object.keys(brut).forEach((k) => {
      if (k.endsWith('_required')) body[k] = toBool(formSite[k]);
      else if (k === 'payment_terms_days') body[k] = formSite[k] === '' ? null : Number(formSite[k]);
      else body[k] = brut[k];
    });
    if (!Object.keys(body).length) { setSuccess('Rien n\'a changé, rien n\'a été envoyé.'); return; }
    setSending(true); setError(''); setSuccess('');
    try {
      const r = await api.put(`/invoices/sites/${siteId}/facturation`, body);
      setSiteBilling(r.data);
      const f = { ...EMPTY_SITE_REFERENCES };
      Object.keys(EMPTY_SITE_REFERENCES).forEach((k) => {
        if (k.endsWith('_required')) f[k] = triState(r.data[k]);
        else f[k] = r.data[k] === null || r.data[k] === undefined ? '' : r.data[k];
      });
      setFormSite(f); setInitSite(f);
      setSuccess(`Paramètres du site enregistrés : ${Object.keys(body).join(', ')}.`);
    } catch (e) {
      setError(billingErrorMessage(e, 'Enregistrement refusé'));
    } finally {
      setSending(false);
    }
  };

  const isPublic = PUBLIC_ENTITY_KINDS.includes(identity?.entity_kind);
  const directoryNotChecked = siteBilling
    && (siteBilling.chorus_service_code_required === null || siteBilling.chorus_engagement_required === null);

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        Seuls les champs réellement modifiés sont envoyés. L&apos;API applique une
        écriture partielle : un champ vidé volontairement efface la valeur en base,
        un champ non touché reste intact.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Organisation</InputLabel>
            <Select value={orgId} label="Organisation" onChange={(e) => setOrgId(e.target.value)}>
              <MenuItem value="">Choisir</MenuItem>
              {orgs.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth size="small" disabled={!sites.length}>
            <InputLabel>Établissement</InputLabel>
            <Select value={siteId} label="Établissement" onChange={(e) => setSiteId(e.target.value)}>
              <MenuItem value="">Choisir</MenuItem>
              {sites.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {isLoading && <CircularProgress size={20} sx={{ mb: 2 }} />}

      {identity && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Identité de facturation, organisation</Typography>
          {!identity.entity_kind && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              La nature juridique n&apos;est pas posée. C&apos;est la clé de voûte :
              elle décide du canal d&apos;émission et du délai légal, et tant
              qu&apos;elle manque, l&apos;émission refusera de partir.
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Raison sociale exacte" value={orgForm.legal_name}
                onChange={(e) => setOrgForm((f) => ({ ...f, legal_name: e.target.value }))}
                helperText="Telle qu'elle figure au registre, elle sera reprise sur la facture" />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" label="Forme juridique" value={orgForm.legal_form}
                onChange={(e) => setOrgForm((f) => ({ ...f, legal_form: e.target.value }))} />
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Nature juridique</InputLabel>
                <Select value={orgForm.entity_kind} label="Nature juridique"
                  onChange={(e) => setOrgForm((f) => ({ ...f, entity_kind: e.target.value }))}>
                  <MenuItem value="">{NOT_PROVIDED}</MenuItem>
                  {Object.entries(ENTITY_KINDS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="SIRET" value={orgForm.siret}
                inputProps={{ maxLength: 14 }}
                onChange={(e) => setOrgForm((f) => ({ ...f, siret: e.target.value }))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="TVA intracommunautaire" value={orgForm.vat_number}
                inputProps={{ maxLength: 20 }}
                onChange={(e) => setOrgForm((f) => ({ ...f, vat_number: e.target.value }))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Courriel de facturation" value={orgForm.billing_email}
                onChange={(e) => setOrgForm((f) => ({ ...f, billing_email: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Adresse de facturation" value={orgForm.billing_address}
                onChange={(e) => setOrgForm((f) => ({ ...f, billing_address: e.target.value }))} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" label="Ville" value={orgForm.billing_city}
                onChange={(e) => setOrgForm((f) => ({ ...f, billing_city: e.target.value }))} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" label="Code postal" value={orgForm.billing_postal_code}
                inputProps={{ maxLength: 10 }}
                onChange={(e) => setOrgForm((f) => ({ ...f, billing_postal_code: e.target.value }))} />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <Button variant="contained" size="small" disabled={sending} onClick={saveOrg}>
              {sending ? <CircularProgress size={16} /> : 'Enregistrer l\'identité'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Canal applicable selon la nature retenue : {CHANNEL_LABEL[identity.canal_applicable] || NOT_PROVIDED}
              {isPublic ? ', et un établissement public ne se prélève pas.' : ''}
            </Typography>
          </Box>
        </Paper>
      )}

      {siteBilling && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Paramètres de facturation, établissement {siteBilling.name}
          </Typography>
          {directoryNotChecked && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Annuaire du portail non consulté</AlertTitle>
              Le caractère obligatoire du code service et du numéro d&apos;engagement
              n&apos;est pas renseigné. Ce n&apos;est pas un « non » : tant que ces deux
              questions restent sans réponse, l&apos;émission d&apos;une facture publique
              est refusée. La réponse se lit dans l&apos;annuaire des structures du portail.
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="SIRET de l'établissement" value={formSite.siret}
                inputProps={{ maxLength: 14 }}
                onChange={(e) => setFormSite((f) => ({ ...f, siret: e.target.value }))}
                helperText="Clé de routage vers l'entité destinataire" />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Code service exigé</InputLabel>
                <Select value={formSite.chorus_service_code_required} label="Code service exigé"
                  onChange={(e) => setFormSite((f) => ({ ...f, chorus_service_code_required: e.target.value }))}>
                  <MenuItem value="">Annuaire non consulté</MenuItem>
                  <MenuItem value="oui">Oui, exigé</MenuItem>
                  <MenuItem value="non">Non, pas exigé</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Code service" value={formSite.chorus_service_code}
                onChange={(e) => setFormSite((f) => ({ ...f, chorus_service_code: e.target.value }))} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Engagement exigé</InputLabel>
                <Select value={formSite.chorus_engagement_required} label="Engagement exigé"
                  onChange={(e) => setFormSite((f) => ({ ...f, chorus_engagement_required: e.target.value }))}>
                  <MenuItem value="">Annuaire non consulté</MenuItem>
                  <MenuItem value="oui">Oui, exigé</MenuItem>
                  <MenuItem value="non">Non, pas exigé</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Numéro d'engagement" value={formSite.engagement_number}
                inputProps={{ maxLength: 50 }}
                onChange={(e) => setFormSite((f) => ({ ...f, engagement_number: e.target.value }))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Numéro de marché" value={formSite.market_number}
                inputProps={{ maxLength: 80 }}
                onChange={(e) => setFormSite((f) => ({ ...f, market_number: e.target.value }))} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Délai de paiement</InputLabel>
                <Select value={formSite.payment_terms_days} label="Délai de paiement"
                  onChange={(e) => setFormSite((f) => ({ ...f, payment_terms_days: e.target.value }))}>
                  <MenuItem value="">Non constaté, le délai légal s&apos;applique</MenuItem>
                  {PAYMENT_TERMS.map((d) => <MenuItem key={d.v} value={d.v}>{d.l}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Contact facturation" value={formSite.billing_contact_name}
                onChange={(e) => setFormSite((f) => ({ ...f, billing_contact_name: e.target.value }))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Courriel du contact" value={formSite.billing_contact_email}
                onChange={(e) => setFormSite((f) => ({ ...f, billing_contact_email: e.target.value }))} />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <Button variant="contained" size="small" disabled={sending} onClick={saveSite}>
              {sending ? <CircularProgress size={16} /> : 'Enregistrer les paramètres du site'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Délai réellement applicable, calculé par l&apos;API :
              {siteBilling.delai_applicable ? ` ${siteBilling.delai_applicable} jours` : ` inconnu, la nature juridique manque`}
            </Typography>
          </Box>
        </Paper>
      )}

      {!orgId && <Empty texte="Choisir une organisation pour lire et compléter ses références de facturation." />}
    </Box>
  );
}
